import React, { useState } from "react";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { createFinanceCategory } from "../../../services/expenseService";
import { appApi, useGetFinanceCategoriesQuery } from "../../../store/appApi";

const EMPTY_FORM = { categoryName: "", description: "" };

const CreateHead = () => {
  const dispatch = useDispatch();

  // ── Form state ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ── Remote data — full category records for the management table ────────────
  const {
    data: catData,
    isLoading: catLoading,
    isFetching: catFetching,
  } = useGetFinanceCategoriesQuery();

  const categories = Array.isArray(catData?.financecategory)
    ? catData.financecategory
    : Array.isArray(catData?.list)
      ? catData.list
      : Array.isArray(catData)
        ? catData
        : [];

  const tableLoading = catLoading || catFetching;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.categoryName.trim())
      e.categoryName = "Category name is required.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await createFinanceCategory({
        categoryName: form.categoryName.trim(),
        ...(form.description.trim()
          ? { description: form.description.trim() }
          : {}),
      });

      // Bust RTK cache so the table re-fetches automatically
      dispatch(appApi.util.invalidateTags(["FinanceCategories"]));

      setForm(EMPTY_FORM);
      setErrors({});

      Swal.fire({
        icon: "success",
        title: "Category Created",
        text: `"${form.categoryName.trim()}" has been added as a category head.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to create category. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  return (
    <>
      <PageTitle activeMenu="Category Heads" motherMenu="Finance" />

      <div className="row g-3">
        {/* ── Add Form ────────────────────────────────────────────────────────── */}
        <div className="col-xl-4 col-lg-5">
          <div className="card" style={{ position: "sticky", top: 0 }}>
            <div className="card-header">
              <h5 className="card-title mb-0">Add New Category Head</h5>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate>
                {/* Category Name */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="categoryName">
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <input
                    id="categoryName"
                    name="categoryName"
                    type="text"
                    className={`form-control ${errors.categoryName ? "is-invalid" : ""}`}
                    placeholder="e.g. Office Supplies, Fuel"
                    value={form.categoryName}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.categoryName && (
                    <div className="invalid-feedback">
                      {errors.categoryName}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="form-group mb-4">
                  <label className="form-label" htmlFor="description">
                    Description{" "}
                    <span className="text-muted small fw-normal">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control"
                    rows={3}
                    placeholder="Brief description of this expense head…"
                    value={form.description}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : (
                    "Save Category Head"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* ── Category Table ───────────────────────────────────────────────────── */}
        <div className="col-xl-8 col-lg-7">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">Existing Category Heads</h5>
              {!tableLoading && (
                <span className="badge badge-primary">{categories.length}</span>
              )}
            </div>

            <div className="card-body" style={{ overflowY: "auto", maxHeight: "calc(100vh - 250px)" }}>
              <div className="table-responsive" style={{ minWidth: 0 }}>
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category Name</th>
                      <th>Added By</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading && (
                      <tr>
                        <td colSpan={4} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}

                    {!tableLoading && categories.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          No category heads found. Use the form to add one.
                        </td>
                      </tr>
                    )}

                    {!tableLoading &&
                      categories.map((cat, idx) => (
                        <tr key={cat._id || idx}>
                          <td className="text-muted small">{idx + 1}</td>
                          <td className="fw-semibold">
                            {cat.categoryName || cat.name || "—"}
                          </td>
                          <td className="text-muted small">
                            {cat.user || "—"}
                          </td>
                          <td className="text-muted small">
                            {formatDate(cat.createdAt || cat.created)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateHead;
